import { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
  deleteUserTableConfigs,
  getUserTableConfigs,
  getUserTableConfigsByReportKey,
  saveUserTableConfigs,
  updateUserTableConfigs,
} from 'api/user/user-table-configs';

import { getTableConfigs, setTableConfigs } from 'store/globalApp';

import {
  getUserConfigRequest,
  processCheckTableConfig,
} from 'components/shared/CustomTable/helpers-config';

import type { Column, DataConfigTable } from 'api/shared/models';
import type { UserConfig } from 'api/user/models';

const useTableConfig = () => {
  const dispatch = useDispatch();

  const [showLoader, setShowLoader] = useState(false);
  const tableConfigs = useSelector(getTableConfigs);

  const updateTableConfigsByData = (data: UserConfig) => {
    const newtTableConfigs = tableConfigs.map((tableConfig) => {
      if (tableConfig.reportKey === data.reportKey) {
        return data;
      }
      return tableConfig;
    });
    dispatch(setTableConfigs(newtTableConfigs));
  };

  const deleteTableConfigByReportKey = (reportKey: string) => {
    const newtTableConfigs = tableConfigs.filter(
      (tableConfig) => tableConfig.reportKey !== reportKey,
    );
    dispatch(setTableConfigs([...newtTableConfigs]));
  };

  const addTableConfigsByData = (data: UserConfig) => {
    const tableConfig = tableConfigs.find(
      (itemTableConfig) => itemTableConfig.reportKey === data.reportKey,
    );
    if (tableConfig) {
      updateTableConfigsByData(data);
      return;
    }
    dispatch(setTableConfigs([...tableConfigs, data]));
  };

  const loadTableConfigurations = async () => {
    setShowLoader(true);
    const { success, data } = await getUserTableConfigs();
    setShowLoader(false);
    if (success && data) {
      dispatch(setTableConfigs(data.data));
    }
  };

  const getTableConfigurationById = async (
    tableName: string,
  ): Promise<DataConfigTable | null> => {
    const dataConfigTable = tableConfigs.find(
      (tableConfig) => tableConfig.reportKey === tableName,
    );
    if (dataConfigTable) return dataConfigTable.columnConfiguration;

    setShowLoader(true);
    const { success, data } = await getUserTableConfigsByReportKey(tableName);
    setShowLoader(false);
    if (success && data) {
      addTableConfigsByData(data.data);
      return data.data.columnConfiguration;
    }
    return null;
  };

  const saveTableConfiguration = async (
    tableName: string,
    columns: Column[],
    configVersion: number,
  ): Promise<DataConfigTable> => {
    const bodyConfig = getUserConfigRequest(tableName, configVersion, columns);
    setShowLoader(true);
    const { success, data } = await saveUserTableConfigs(bodyConfig);
    setShowLoader(false);
    if (success && data && data.data) {
      addTableConfigsByData(data.data);
      return data.data.columnConfiguration;
    }
    return bodyConfig.columnConfiguration;
  };

  const updateTableConfiguration = async (
    data: DataConfigTable,
  ): Promise<boolean> => {
    const bodyConfig = {
      reportKey: data.tableName,
      columnConfiguration: data,
    };
    setShowLoader(true);
    const { success, data: dataResponse } =
      await updateUserTableConfigs(bodyConfig);
    if (dataResponse) {
      updateTableConfigsByData(dataResponse.data);
    }
    setShowLoader(false);
    return success;
  };

  const deleteTableConfiguration = async (
    reportKey: string,
  ): Promise<boolean> => {
    setShowLoader(true);
    const { success } = await deleteUserTableConfigs(reportKey);
    if (success) {
      deleteTableConfigByReportKey(reportKey);
    }

    setShowLoader(false);
    return success;
  };

  const processGetTableConfiguration = async (
    tableName: string,
    columns: Column[],
    configVersion: number,
  ): Promise<DataConfigTable> => {
    const tableConfig = await getTableConfigurationById(tableName);
    if (tableConfig) {
      const bodyConfig = getUserConfigRequest(
        tableName,
        configVersion,
        columns,
      );

      if (tableConfig.version !== configVersion) {
        await updateTableConfiguration(bodyConfig.columnConfiguration);
        return bodyConfig.columnConfiguration;
      }

      const respCheck = processCheckTableConfig(tableConfig, columns);

      if (respCheck.requiresUpdate) {
        await updateTableConfiguration(respCheck.data);
        return respCheck.data;
      }
      return tableConfig;
    }

    const tableConfigSave = await saveTableConfiguration(
      tableName,
      columns,
      configVersion,
    );

    return tableConfigSave;
  };

  return {
    showLoader,
    getTableConfigurationById,
    loadTableConfigurations,
    processGetTableConfiguration,
    saveTableConfiguration,
    updateTableConfiguration,
    deleteTableConfiguration,
    deleteTableConfigByReportKey,
  };
};

export default useTableConfig;
